import { isString } from "generic-type-guard";
import * as React from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";

import { ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import {
	DataSelectors,
	ModelSelectors,
	UiStateSelectors
} from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import { FormModel, FormModelPath } from "@com.mgmtp.a12.formengine/formengine-core/lib/models";
import { DocumentModel, DocumentServiceFactory } from "@com.mgmtp.a12.kernel/kernel-md-facade";
import {
	DefaultFormModelMap,
	FormModelMap,
	useDocumentPathForInput
} from "@com.mgmtp.a12.formengine/formengine-core/lib/view";
import { LocalizerContext } from "@com.mgmtp.a12.utils/utils-localization-react/lib/main";
import { LoggerFactory } from "@com.mgmtp.a12.utils/utils-logging";

import { ComponentContext } from "../../../component/ComponentContext";
import { VisuallyHidden } from "../../../component/VisuallyHidden";
import { RESOURCE_KEYS } from "../../../localization";
import { isValidationInfo } from "../../../utilities/typeGuardUtils";
import { COMPONENT, LINKS_MARKER } from "../../../utilities/variables/modulfRuntimeAnnotations";

import { ControlContext, ModulFDataContext } from "./contexts";
import { ModulFLink, ModulFLinkContext } from "./customHintTooltip";
import { isStrikethroughControl } from "./strikethrough";

const logger = LoggerFactory.getLogger("modulf");

export function CustomControl(
	activityId: string
): React.ComponentType<FormModelMap.FormModelComponentProps<FormModel.Control>> {
	return function CustomControl(props) {
		const dataHolder = useSelector(
			ActivitySelectors.dataHolderByDescriptor(activityId, { validationInfo: "validationInfo" })
		);
		const data = dataHolder?.data;

		const requiredFields = isValidationInfo(data) ? data.requiredFields : undefined;

		const { localizer, conversion } = React.useContext(LocalizerContext);
		const document = DataSelectors.document()(props.config.renderOptions.state);
		const screenPath = UiStateSelectors.currentScreenLocation()(props.config.renderOptions.state).path;
		const formModel = ModelSelectors.formModel()(props.config.renderOptions.state);
		const documentPath = useDocumentPathForInput(
			props.modelElement.elementPath,
			props.config.renderOptions.state.models.documentModel
		);
		const strikethrough = React.useMemo(
			() =>
				isStrikethroughControl(
					formModel,
					props.config.parentPath,
					props.modelElement,
					document,
					screenPath,
					documentPath
				),
			[formModel, props.config.parentPath, props.modelElement, document, screenPath, documentPath]
		);

		const linksValue = props.modelElement.annotations?.find(({ name }) => name === LINKS_MARKER)?.value;
		const linksData: ModulFLink[] | undefined = React.useMemo(() => {
			if (!(linksValue && isString(linksValue))) {
				return undefined;
			} else {
				try {
					return JSON.parse(linksValue) as ModulFLink[];
				} catch (e) {
					logger.log(e);
					return undefined;
				}
			}
		}, [linksValue]);

		const documentModel = props.config.renderOptions.state.models.documentModel;
		const field = new DocumentServiceFactory()
			.getDocumentModelSearchService(documentModel)
			.getByPath(props.modelElement.elementPath);
		const isEpayblAmount =
			field?.type === "Field" &&
			field.fieldType.type === "NumberType" &&
			field.annotations?.some(
				annotation =>
					(annotation.name === "mf_runtime_epaybl_zahlungsfeld" && annotation.value === "BetragFinal") ||
					(annotation.name === "modulf_label" && annotation.value === "epaymentBetragAngepasst")
			);
		const value = {
			isRequired: !!requiredFields?.includes(props.modelElement.elementRef),
			onEpayblAmountChange:
				isEpayblAmount && field?.type === "Field"
					? (input: string) => {
							const result = conversion.parseValue(input.trim(), {
								...DocumentModel.extractConversionConfig(field.fieldType),
								modelId: documentModel.header.id,
								modelPath: props.modelElement.elementPath
							});
							props.config.renderOptions.eventHandlers.onValueChange(
								documentPath,
								result.parseError ? null : (result.value ?? null),
								FormModelPath.extend(props.config.parentPath, props.modelElement)
							);
							if (result.parseError) {
								props.config.renderOptions.eventHandlers.onParseError(
									documentPath,
									input,
									result.parseError
								);
							}
							return !result.parseError;
						}
					: undefined
		};

		const component = props.modelElement.annotations?.find(it => it.name === COMPONENT)?.value;
		return (
			<ModulFLinkContext.Provider value={linksData}>
				<ControlContext.Provider value={value}>
					<ModulFDataContext.Provider value={documentPath}>
						<ComponentContext.Provider value={component}>
							<StyledControlWrapper requiredField={value.isRequired} strikethrough={strikethrough}>
								<DefaultFormModelMap.Control.component {...props} />
								{strikethrough && (
									<VisuallyHidden>
										{localizer({ key: RESOURCE_KEYS.features.control.strikethroughHint })}
									</VisuallyHidden>
								)}
							</StyledControlWrapper>
						</ComponentContext.Provider>
					</ModulFDataContext.Provider>
				</ControlContext.Provider>
			</ModulFLinkContext.Provider>
		);
	};
}

/**
 * Pflichtfelder (label::after) werden mit einem * markiert.
 * Bei Radiobuttons (.field__control--boolean) jedoch soll bei den einzelnen Radiobutton kein * angezeigt werden, da nur
 *  einmal eine Auswahl erfolgt und nicht jedes einzelne Auswahlfeld.
 * Eine Checkbox hat jedoch ebenfalls diese Eigenschaft wie der Radiobutton, aber soll trotzdem eine *-Markierung bekommen.
 * strikethrough streicht den angezeigten Wert eines readonly Feldes, wenn dieser durch ein anderes Feld
 * überschrieben wurde. Die textuelle Information dazu wird zusätzlich per VisuallyHidden-Hinweis bereitgestellt,
 * damit die Bedeutung nicht ausschließlich visuell vermittelt wird.
 */
const StyledControlWrapper = styled.div<{ requiredField: boolean; strikethrough: boolean }>`
	& label::after {
		${props => (props.requiredField ? "content: ' *'; display:inline" : "")}
	}
	& .field__control--boolean label::after {
		display: none;
	}
	& .field__control--boolean label[data-role="checkbox-label"]::after {
		display: inline;
	}
	${props =>
		props.strikethrough &&
		`
		& input, & .field__control-value {
			text-decoration: line-through;
			text-decoration-style: solid;
			text-decoration-thickness: 1px;
			opacity: 0.7;
		}
	`}
`;
